/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {Observable} from 'rxjs';
import {SupplyingPackingPlantBaseContract} from './contracts/supplying-packing-plant-base.contract';
import { HttpClient } from '@angular/common/http';
import {Cacheable} from '../../decorators/cacheable.decorator';
import {ShipToPartyBaseModel} from './contracts/ship-to-party-base.model';
import {RouteSearchCriteriaModel} from './contracts/route-search-criteria.model';
import {PartnerBaseModel} from './contracts/partner/model/partner-base.model';
import {PartnerGroupBaseModel} from './contracts/partner-group-base.model';
import {TechnicianGroupBaseContract} from './contracts/technician-group-base.contract';
import {PartNumberChangeDashboardModel} from './contracts/part-number-change-dashboard.model';

@Injectable({
    providedIn: 'root'
})
export class ReferenceDataApi {
    private routeDataBaseUrl = `${environment.partNumberChangesBaseUrl}/api/route`;
    private routePartnerUrl = `${this.routeDataBaseUrl}/partner/base/active`;
    private routeShipToPartyUrl = `${this.routeDataBaseUrl}/shipToParty/base/active`;
    private routeSupplyingPackingPlantUrl = `${this.routeDataBaseUrl}/supplyingPackingPlant/base/active`;
    private partnerUrl = `${environment.partNumberChangesBaseUrl}/api/partner`;
    private technicianGroupUrl = `${environment.partNumberChangesBaseUrl}/api/technicianGroup`;
    private supplyingPackingPlantUrl = `${environment.partNumberChangesBaseUrl}/api/supplyingPackingPlant`;
    private routeTechnicianGroupUrl = `${this.routeDataBaseUrl}/technicianGroup/base/active`;
    private dashboardUrl = `${environment.partNumberChangesBaseUrl}/api/partNumberChange`;

    constructor(protected http: HttpClient) {
    }

    /**
     * This method retrieves the Partners
     * @param routeSearchCriteria The search criteria
     */
    @Cacheable()
    getActivePartners(routeSearchCriteria: RouteSearchCriteriaModel): Observable<PartnerBaseModel[]> {
        return this.http.post<PartnerBaseModel[]>(`${this.routePartnerUrl}`, routeSearchCriteria);
    }

    /**
     * This method retrieves all the Partners grouped by countries
     */
    @Cacheable()
    getAllPartnersGroupedByCountries(): Observable<PartnerGroupBaseModel[]> {
        return this.http.get<PartnerGroupBaseModel[]>(`${this.partnerUrl}/base/groupBy/country`);
    }

    /**
     * This method retrieves the Ship to Parties
     * @param routeSearchCriteria The search criteria
     */
    @Cacheable()
    getActiveShipToParties(routeSearchCriteria: RouteSearchCriteriaModel): Observable<ShipToPartyBaseModel[]> {
        return this.http.post<ShipToPartyBaseModel[]>(`${this.routeShipToPartyUrl}`, routeSearchCriteria);
    }

    /**
     * This method retrieves the active Supplying Packing Plants by Partner and Ship to Party
     * @param routeSearchCriteria The search criteria
     */
    @Cacheable()
    getActiveSupplyingPackingPlants(routeSearchCriteria: RouteSearchCriteriaModel): Observable<SupplyingPackingPlantBaseContract[]> {
        return this.http.post<SupplyingPackingPlantBaseContract[]>(`${this.routeSupplyingPackingPlantUrl}`, routeSearchCriteria);
    }

    /**
     * This method retrieved all the Technician Groups
     */
    @Cacheable()
    getAllTechnicianGroups(): Observable<TechnicianGroupBaseContract[]> {
        return this.http.get<TechnicianGroupBaseContract[]>(`${this.technicianGroupUrl}/base`);
    }

    /**
     * This method retrieves the Technician Groups
     * @param routeSearchCriteria The search criteria
     */
    @Cacheable()
    getActiveTechnicianGroups(routeSearchCriteria: RouteSearchCriteriaModel): Observable<TechnicianGroupBaseContract[]> {
        return this.http.post<TechnicianGroupBaseContract[]>(`${this.routeTechnicianGroupUrl}`, routeSearchCriteria);
    }

    /**
     * This method retrieved all the Technician Groups
     */
    @Cacheable()
    getAllSupplyingPackingPlants(): Observable<SupplyingPackingPlantBaseContract[]> {
        return this.http.get<SupplyingPackingPlantBaseContract[]>(`${this.supplyingPackingPlantUrl}/base`);
    }

    getDashboard(): Observable<PartNumberChangeDashboardModel> {
        return this.http.get<PartNumberChangeDashboardModel>(`${this.dashboardUrl}/dashboard`);
    }
}
